/** The module's paths, declared once for both host route trees and every link. */
export const FUNDRAISERS_SLUG = 'fundraisers';
export const FUNDRAISER_EDITOR_PATH = '/fundraisers/$fundraiserId';
export const FUNDRAISER_CAMPAIGN_PATH = '/fundraisers/$fundraiserId/campaign';
/** A campaign's Layers tab (FCM-WEB25): where it sits and the campaigns under it. */
export const FUNDRAISER_LAYERS_PATH = '/fundraisers/$fundraiserId/layers';
/** A fundraiser's Donors tab (FCM-WEB49): its organizers' private list of its gifts. */
export const FUNDRAISER_DONORS_PATH = '/fundraisers/$fundraiserId/donors';
/** A fundraiser's Messages tab (FCM-WEB50): its organization's messages to its donors. */
export const FUNDRAISER_MESSAGES_PATH = '/fundraisers/$fundraiserId/messages';
/** A fundraiser's Emails tab (EMC-WEB1): the series of emails the organization sends about it. */
export const FUNDRAISER_EMAILS_PATH = '/fundraisers/$fundraiserId/emails';
/** A campaign's Social tab (EMC-WEB5): its posts, and Social's composer rented in place. */
export const FUNDRAISER_SOCIAL_PATH = '/fundraisers/$fundraiserId/social';
/** One email of the series: its preview beside who it reaches and when it sends (EMC-WEB1). */
export const FUNDRAISER_EMAIL_PATH = '/fundraisers/$fundraiserId/emails/$emailId';
/** One email's designer, written with the assistant beside a live preview (EMC-WEB10). */
export const FUNDRAISER_EMAIL_DESIGN_PATH = '/fundraisers/$fundraiserId/emails/$emailId/design';
/** A campaign's Board Ask (EMC-WEB4, frame 21): the ask to the board, drafted from the campaign. */
export const FUNDRAISER_BOARD_ASK_PATH = '/fundraisers/$fundraiserId/board-ask';
/** The fundraiser's QR design page (FCM-WEB4) — the `campaign` child precedent. */
export const FUNDRAISER_QR_PATH = '/fundraisers/$fundraiserId/qr';
/** A season's setup (FCM-WEB34): its games made in one act, opened from its Layers tab. */
export const FUNDRAISER_SEASON_PATH = '/fundraisers/$fundraiserId/season';
/** A game's Bonus pledges tab (FCM-WEB35): its bonus pledges, added and managed during the game. */
export const FUNDRAISER_BONUS_PLEDGES_PATH = '/fundraisers/$fundraiserId/bonus-pledges';
export const FUNDRAISERS_ROUTES = {
  list: '/fundraisers',
  /** The list page's "Started by supporters" tab: the list route's own address plus its search. */
  supporters: '/fundraisers?tab=supporters',
  create: '/fundraisers/new',
  team: '/fundraisers/team',
  signIn: '/fundraisers/sign-in',
  editor: (id: string) => `/fundraisers/${encodeURIComponent(id)}`,
  campaign: (id: string) => `/fundraisers/${encodeURIComponent(id)}/campaign`,
  layers: (id: string) => `/fundraisers/${encodeURIComponent(id)}/layers`,
  donors: (id: string) => `/fundraisers/${encodeURIComponent(id)}/donors`,
  messages: (id: string) => `/fundraisers/${encodeURIComponent(id)}/messages`,
  emails: (id: string) => `/fundraisers/${encodeURIComponent(id)}/emails`,
  social: (id: string) => `/fundraisers/${encodeURIComponent(id)}/social`,
  /** An email's page, opened on one of its panels (`?step=audience` from the designer's Continue). */
  email: (id: string, emailId: string, step?: EmailStep) =>
    `/fundraisers/${encodeURIComponent(id)}/emails/${encodeURIComponent(emailId)}${
      step === undefined ? '' : `?step=${step}`
    }`,
  emailDesign: (id: string, emailId: string) =>
    `/fundraisers/${encodeURIComponent(id)}/emails/${encodeURIComponent(emailId)}/design`,
  qr: (id: string) => `/fundraisers/${encodeURIComponent(id)}/qr`,
  boardAsk: (id: string) => `/fundraisers/${encodeURIComponent(id)}/board-ask`,
  season: (id: string) => `/fundraisers/${encodeURIComponent(id)}/season`,
  bonusPledges: (id: string) => `/fundraisers/${encodeURIComponent(id)}/bonus-pledges`,
} as const;

/** Which panel an email's page opens on: who it reaches, or the test and when it sends. */
export type EmailStep = 'audience' | 'schedule';

/** The email route's search: only a known `step` is kept; anything else opens on the audience. */
export function parseEmailSearch(search: Record<string, unknown>): { step?: EmailStep } {
  return search.step === 'audience' || search.step === 'schedule' ? { step: search.step } : {};
}

/** Which tab the Fundraisers page shows. The organization's own list is the page's plain address. */
export type FundraisersView = 'own' | 'supporters';

/** The list route's search: only `tab=supporters` is kept, and anything else reads as the own tab. */
export interface FundraisersListSearch {
  tab?: 'supporters';
}

/** The list route's `validateSearch` — each host that can show the tab owns the word through it. */
export function parseFundraisersListSearch(search: Record<string, unknown>): FundraisersListSearch {
  return search.tab === 'supporters' ? { tab: 'supporters' } : {};
}

/** The page's view off the route's parsed search. */
export function fundraisersViewFromSearch(search: FundraisersListSearch): FundraisersView {
  return search.tab === 'supporters' ? 'supporters' : 'own';
}

/**
 * Whether an in-app href lies inside the module's own subtree — `/fundraisers` and every page beneath
 * it. A host's `onNavigate` uses it to tell a hop INSIDE the module, which carries no frame of its own
 * (the page keeps the frame it wears, a borrowing module's included), from a door OUT of the module.
 */
export function isFundraisersPath(href: string): boolean {
  const { pathname } = new URL(href, 'http://in-app.invalid');
  return pathname === FUNDRAISERS_ROUTES.list || pathname.startsWith(`${FUNDRAISERS_ROUTES.list}/`);
}

export function publicJoinUrl(base: string, id: string): string {
  return `${base.replace(/\/$/, '')}/p2p/campaign/${encodeURIComponent(id)}/join`;
}
